import React from 'react'
import { inject, observer } from 'mobx-react'
import { withTranslation } from 'react-i18next'
import Icon from 'components/common/Icon'
import { getIconElement } from './poiIconUtils'

const PoiBucketLists = ({ dataStore, uiStore, selectionStore, t }) => {
  const poi = dataStore && dataStore.poi
  const allMarkers = (poi && poi.getMarkersForBucketDisplay)
    ? poi.getMarkersForBucketDisplay()
    : ((poi && poi.temporaryMarkers) || [])

  const removeFrom = (id, bucket) => { if (poi && poi.removeFromBucket) poi.removeFromBucket(id, bucket) }

  const viewFilter = poi ? poi.viewFilter : 'all'
  const bucketsToShow = viewFilter === 'all'
    ? ['general', 'ally', 'competitor']
    : [viewFilter]

  return (
    <div>
      {bucketsToShow.map((bucketType) => {
        const itemsInBucket = allMarkers.filter(m => {
          // Check if this item was explicitly removed from this bucket
          const removedBuckets = poi && poi.removedFromBucketById && poi.removedFromBucketById.get(m.id)
          if (removedBuckets && removedBuckets.includes(bucketType)) {
            return false // Item was removed, don't show it
          }

          // Check current state first, then fallback to baseline (persisted) state
          const currentBuckets = poi && poi.bucketById && poi.bucketById.get(m.id)
          const baselineBuckets = poi && poi.sharedBucketsById && poi.sharedBucketsById.get(m.id)
          const buckets = currentBuckets || baselineBuckets
          return buckets ? buckets.includes(bucketType) : false
        })

        const displayBucketName = {
          general: t('POI.BUCKET_GENERAL'),
          ally: t('POI.BUCKET_ALLIES'),
          competitor: t('POI.BUCKET_COMPETITORS')
        }

        return (
          <div key={bucketType} className={`search-results__bucket-card search-results__bucket-card--${bucketType}`}>
            <div className='search-results__bucket-head'>
              <span>{displayBucketName[bucketType]}</span>
              <span className='search-results__count'>{itemsInBucket.length}</span>
            </div>

            {itemsInBucket.map((m) => {
              const currentBucket = bucketType
              const isMenuOpen = poi && poi.openMenuId === m.id && poi.openMenuSource === 'bucket'

              const handleRowMouseEnter = () => {
                if (!uiStore || m == null) return
                const idStr = String(m.id || '')
                const hoverTargetId = m.savedMarkerId ||
                  (idStr.startsWith('saved_') ? parseInt(idStr.replace('saved_', ''), 10) : m.id)

                if (hoverTargetId == null) return
                if (typeof uiStore.setHoveredMarkerForZIndex === 'function') {
                  uiStore.setHoveredMarkerForZIndex(hoverTargetId)
                }
                if (typeof uiStore.setHoveredPoiMarkerForAnimation === 'function') {
                  uiStore.setHoveredPoiMarkerForAnimation(hoverTargetId)
                }
              }

              const handleRowMouseLeave = () => {
                if (!uiStore || m == null) return
                const idStr = String(m.id || '')
                const hoverTargetId = m.savedMarkerId ||
                  (idStr.startsWith('saved_') ? parseInt(idStr.replace('saved_', ''), 10) : m.id)

                if (hoverTargetId == null) return
                if (typeof uiStore.clearHoveredMarkerForZIndex === 'function') {
                  uiStore.clearHoveredMarkerForZIndex(hoverTargetId)
                }
                if (typeof uiStore.clearHoveredPoiMarkerForAnimation === 'function') {
                  uiStore.clearHoveredPoiMarkerForAnimation(hoverTargetId)
                }
              }

              const handleZoomToMarker = () => {
                if (!uiStore || !uiStore.map || !uiStore.map.leafletElement) return
                const pos = m.position && m.position.latLng
                if (!pos || pos.lat == null || pos.lng == null) return

                // Normalize marker id: bucket markers may use "saved_123" + savedMarkerId,
                // while the actual map marker uses numeric id (123).
                const idStr = String(m.id || '')
                const activeMarkerId = m.savedMarkerId ||
                  (idStr.startsWith('saved_') ? parseInt(idStr.replace('saved_', ''), 10) : m.id)

                // Mark this as the active marker for z-index/tooltip purposes
                if (activeMarkerId != null) {
                  if (typeof uiStore.setClickedMarker === 'function') {
                    uiStore.setClickedMarker(activeMarkerId)
                  }
                  if (typeof uiStore.setHoveredMarkerForZIndex === 'function') {
                    uiStore.setHoveredMarkerForZIndex(activeMarkerId)
                  }
                }

                // All POI rows use the same zoom path, even at a buffer threshold
                // or after panning the selected shape partly offscreen.
                uiStore.zoomToMarker(pos)
              }

              return (
                <div
                  key={m.id}
                  className='search-results__mini-row'
                  onMouseEnter={handleRowMouseEnter}
                  onMouseLeave={handleRowMouseLeave}
                >
                  <div
                    className='search-results__left'
                    onClick={handleZoomToMarker}
                  >
                    {getIconElement(m)}
                    <div className='search-results__name'>{m.name}</div>
                  </div>

                  {/* Kebab (three-dots) + menu */}
                  <div className='search-results__kebab-wrap'>
                    <button
                      className='search-results__btn search-results__btn--kebab'
                      type='button'
                      aria-expanded={isMenuOpen}
                      aria-haspopup='true'
                      title='Options…'
                      onClick={() => {
                        if (poi && poi.toggleMenu) {
                          poi.openMenuSource = 'bucket'
                          if (poi.menuStepById) poi.menuStepById.set(m.id, 'main')
                          poi.toggleMenu(m.id)
                        }
                      }}
                    >⋮
                    </button>

                    {/* Menu */}
                    {isMenuOpen && (
                      <div className='search-results__menu' role='menu'>
                        {/* Close button */}
                        {poi.menuStepById && poi.menuStepById.get(m.id) === 'categories' && (
                          <button
                            className='search-results__menu-back'
                            style={{
                              position: 'absolute',
                              left: '8px',
                              top: '8px',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '12px',
                              color: '#8C94A0'
                            }}
                            onClick={() => {
                              if (poi.menuStepById) {
                                poi.menuStepById.set(m.id, 'main')
                              }
                            }}
                          >
                            ‹
                          </button>
                        )}
                        <button
                          className='search-results__menu-close'
                          type='button'
                          onClick={() => {
                            if (poi && poi.toggleMenu) poi.toggleMenu(m.id)
                          }}
                        >
                          {t('GLOBAL.CLOSE')}
                        </button>

                        <button
                          type='button'
                          className='search-results__mi'
                          role='menuitem'
                          onClick={() => {
                            removeFrom(m.id, currentBucket)
                            if (poi && poi.toggleMenu) poi.toggleMenu(m.id)
                          }}
                        >
                          Remove
                        </button>

                        {/* Move submenu items */}
                        {(!poi.menuStepById || poi.menuStepById.get(m.id) === 'main')
                          ? (
                            <button
                              className='search-results__mi'
                              style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                paddingBottom: '10px',
                                marginTop: '4px'
                              }}
                              onClick={() => {
                                if (poi && poi.menuStepById) {
                                  poi.menuStepById.set(m.id, 'categories')
                                }
                              }}
                            >
                              <span>Move</span>
                              <Icon id='fa-caret-right' />
                            </button>
                            )
                          : (
                            <div className='search-results__menu-options'>
                              {['general', 'competitor', 'ally'].map((bucket) => {
                                const disabled = bucket === currentBucket
                                const label =
                                bucket === 'general'
                                  ? t('POI.BUCKET_GENERAL')
                                  : (bucket === 'competitor'
                                      ? t('POI.BUCKET_COMPETITORS')
                                      : t('POI.BUCKET_ALLIES'))

                                return (
                                  <button
                                    key={bucket}
                                    className='search-results__menu-option-btn'
                                    disabled={disabled}
                                    style={{
                                      width: '100%',
                                      textAlign: 'left',
                                      background: 'none',
                                      border: 'none',
                                      cursor: disabled ? 'default' : 'pointer',
                                      padding: '8px 12px',
                                      fontSize: '14px',
                                      color: disabled ? '#ccc' : '#292C44',
                                      display: 'block'
                                    }}
                                    onClick={() => {
                                      if (disabled) return
                                      if (poi) {
                                        poi.removeFromBucket(m.id, currentBucket)
                                        poi.addToBucket(m.id, bucket)
                                      }
                                      uiStore.addNotification({
                                        text: t('POI.MOVED_TO_BUCKET', {
                                          name: m.name,
                                          bucket: label
                                        }),
                                        category: 'Success'
                                      })
                                      if (poi) poi.openMenuId = null
                                    }}
                                  >
                                    {label}
                                  </button>
                                )
                              })}
                            </div>
                            )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

export default withTranslation()(
  inject('dataStore', 'uiStore', 'selectionStore')(observer(PoiBucketLists))
)
